import { useEffect } from 'react';

const RISE_PX = 36;
const STAGGER = 0.1;
const SPAN = 0.75;
const DURATION_MS = 3400;
const LG_BREAKPOINT = 1024;

const clamp01 = (value) => Math.min(1, Math.max(0, value));

const easeOutSmooth = (t) => 1 - (1 - t) ** 3;

const segmentProgress = (progress, start, end) =>
  clamp01((progress - start) / (end - start));

const isDesktop = () =>
  typeof window !== 'undefined' && window.innerWidth >= LG_BREAKPOINT;

/** Rise from below — hidden until the scroll reveal runs on all viewports. */
export const staggerLineClass =
  'will-change-[transform,opacity] opacity-0 translate-y-9';

/**
 * Scroll-in staggered lines for `[data-content-line]`.
 */
export const useStaggeredRiseOnView = (containerRef, options = {}) => {
  const {
    durationMs = DURATION_MS,
    risePx,
    distancePx = risePx ?? RISE_PX,
    stagger = STAGGER,
    span = SPAN,
    threshold = 0.22,
    rootMargin = '0px 0px -8% 0px',
    from = 'up',
    replay = false,
    reverseOnLeave = false,
    desktopOnly = false,
    individual = false,
    observeMutations = false,
  } = options;

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return undefined;

    let cleanupDesktop = null;

    const clearInline = () => {
      root.querySelectorAll('[data-content-line]').forEach((el) => {
        el.style.opacity = '';
        el.style.transform = '';
        el.style.transition = '';
        el.style.willChange = '';
      });
    };

    const revealStatic = () => {
      root.querySelectorAll('[data-content-line]').forEach((el) => {
        el.style.opacity = '1';
        el.style.transform = 'none';
        el.style.transition = 'none';
        el.style.willChange = 'auto';
      });
    };

    const setupIndividualDesktop = () => {
      const lineStates = new Map();
      const observers = [];

      const getLines = () =>
        [...root.querySelectorAll('[data-content-line]')].filter(
          (el) => el.getClientRects().length > 0
        );

      const applyLineProgress = (line, value) => {
        const eased = easeOutSmooth(clamp01(value));
        const offset = (1 - eased) * distancePx;
        line.style.opacity = String(eased);
        line.style.transform =
          from === 'left'
            ? `translate3d(${offset}px, 0, 0)`
            : `translate3d(0, ${offset}px, 0)`;
      };

      const stopLineRaf = (line) => {
        const state = lineStates.get(line);
        if (state?.rafId != null) {
          cancelAnimationFrame(state.rafId);
          state.rafId = null;
        }
      };

      const clearLineDelay = (line) => {
        const state = lineStates.get(line);
        if (state?.delayTimer != null) {
          window.clearTimeout(state.delayTimer);
          state.delayTimer = null;
        }
      };

      const animateLineTo = (line, target) => {
        const state = lineStates.get(line);
        if (!state) return;

        stopLineRaf(line);
        const fromP = state.progress;
        const toP = clamp01(target);
        if (Math.abs(fromP - toP) < 0.001) {
          state.progress = toP;
          applyLineProgress(line, toP);
          return;
        }

        const start = performance.now();
        const tick = (now) => {
          const t = clamp01((now - start) / durationMs);
          const eased = easeOutSmooth(t);
          const next = fromP + (toP - fromP) * eased;
          state.progress = next;
          applyLineProgress(line, next);
          if (t < 1) {
            state.rafId = requestAnimationFrame(tick);
            return;
          }
          state.progress = toP;
          applyLineProgress(line, toP);
          state.rafId = null;
        };

        state.rafId = requestAnimationFrame(tick);
      };

      const setupLine = (line) => {
        if (lineStates.has(line)) return;

        lineStates.set(line, {
          progress: 0,
          rafId: null,
          playedOnce: false,
          delayTimer: null,
        });
        applyLineProgress(line, 0);

        const observer = new IntersectionObserver(
          ([entry]) => {
            const state = lineStates.get(line);
            if (!state) return;

            if (entry.isIntersecting) {
              if (!replay && state.playedOnce && state.progress >= 0.999) return;

              const startAnimation = () => {
                state.playedOnce = true;
                if (state.progress < 0.05) applyLineProgress(line, 0);
                animateLineTo(line, 1);
                if (!replay) observer.unobserve(line);
              };

              clearLineDelay(line);
              const delayMs =
                Number.parseInt(line.dataset.revealDelay || '0', 10) || 0;
              if (delayMs > 0) {
                state.delayTimer = window.setTimeout(startAnimation, delayMs);
              } else {
                startAnimation();
              }
              return;
            }

            clearLineDelay(line);

            if (reverseOnLeave && state.progress > 0.01) {
              animateLineTo(line, 0);
              return;
            }

            if (replay) {
              stopLineRaf(line);
              state.progress = 0;
              applyLineProgress(line, 0);
            }
          },
          { threshold, rootMargin }
        );

        observer.observe(line);
        observers.push(observer);
      };

      getLines().forEach(setupLine);

      let mutationObserver = null;
      if (observeMutations) {
        mutationObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType !== Node.ELEMENT_NODE) return;
              if (node.matches?.('[data-content-line]')) setupLine(node);
              node.querySelectorAll?.('[data-content-line]').forEach(setupLine);
            });
          });
        });
        mutationObserver.observe(root, { childList: true, subtree: true });
      }

      return () => {
        mutationObserver?.disconnect();
        observers.forEach((observer) => observer.disconnect());
        lineStates.forEach((state, line) => {
          stopLineRaf(line);
          clearLineDelay(line);
        });
      };
    };

    const setupDesktop = () => {
      if (individual) {
        return setupIndividualDesktop();
      }

      let rafId = null;
      let progress = 0;
      let playedOnce = false;
      let enterInvert = false;
      let lastScrollY = window.scrollY;
      let scrollDir = 1;

      const getLines = () =>
        [...root.querySelectorAll('[data-content-line]')].filter(
          (el) => el.getClientRects().length > 0
        );

      const applyProgress = (value, { invert = enterInvert } = {}) => {
        progress = clamp01(value);
        const lines = getLines();
        const maxDelay = Math.max(0, 1 - span);
        lines.forEach((line, index) => {
          const orderIndex = invert ? lines.length - 1 - index : index;
          const spaced =
            lines.length <= 1 ? 0 : (orderIndex / (lines.length - 1)) * maxDelay;
          const delay = Math.min(orderIndex * stagger, spaced);
          const local = segmentProgress(progress, delay, delay + span);
          const eased = easeOutSmooth(local);
          const signed = invert && from === 'up' ? -1 : 1;
          const offset = (1 - eased) * distancePx * signed;
          line.style.opacity = String(eased);
          line.style.transform =
            from === 'left'
              ? `translate3d(${offset}px, 0, 0)`
              : `translate3d(0, ${offset}px, 0)`;
        });
      };

      const stopRaf = () => {
        if (rafId != null) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
      };

      const animateTo = (target, { invert = enterInvert } = {}) => {
        stopRaf();
        enterInvert = invert;
        const fromP = progress;
        const toP = clamp01(target);
        if (Math.abs(fromP - toP) < 0.001) {
          applyProgress(toP, { invert });
          return;
        }

        const start = performance.now();
        const tick = (now) => {
          const t = clamp01((now - start) / durationMs);
          const eased = easeOutSmooth(t);
          applyProgress(fromP + (toP - fromP) * eased, { invert });
          if (t < 1) {
            rafId = requestAnimationFrame(tick);
            return;
          }
          applyProgress(toP, { invert });
          rafId = null;
        };

        rafId = requestAnimationFrame(tick);
      };

      const onScroll = () => {
        const y = window.scrollY;
        if (y > lastScrollY + 1) scrollDir = 1;
        else if (y < lastScrollY - 1) scrollDir = -1;
        lastScrollY = y;
      };

      applyProgress(0);

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            if (!replay && playedOnce && progress >= 0.999) return;
            playedOnce = true;
            const invert = Boolean(replay && scrollDir < 0);
            if (progress < 0.05) applyProgress(0, { invert });
            animateTo(1, { invert });
            return;
          }

          if (reverseOnLeave && progress > 0.01) {
            animateTo(0, { invert: enterInvert });
            return;
          }

          if (replay) {
            stopRaf();
            applyProgress(0, { invert: enterInvert });
          }
        },
        { threshold, rootMargin }
      );

      window.addEventListener('scroll', onScroll, { passive: true });
      observer.observe(root);

      return () => {
        observer.disconnect();
        window.removeEventListener('scroll', onScroll);
        stopRaf();
      };
    };

    const sync = () => {
      if (cleanupDesktop) {
        cleanupDesktop();
        cleanupDesktop = null;
      }
      clearInline();

      if (desktopOnly && !isDesktop()) {
        revealStatic();
        return;
      }

      cleanupDesktop = setupDesktop();
    };

    sync();
    window.addEventListener('resize', sync);

    return () => {
      window.removeEventListener('resize', sync);
      if (cleanupDesktop) cleanupDesktop();
      clearInline();
    };
  }, [
    containerRef,
    durationMs,
    distancePx,
    stagger,
    span,
    threshold,
    rootMargin,
    from,
    replay,
    reverseOnLeave,
    desktopOnly,
    individual,
    observeMutations,
  ]);
};

export default useStaggeredRiseOnView;
